import { Box, Button, Checkbox, Flex, Input, Stack, Text } from '@chakra-ui/react';
import { useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';

import FigmaFilterIcon from 'components/Icons/FigmaFilterIcon';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import { accent, primaryAction } from 'styles/revampTokens';

const height = 700;
const itemHeight = 30;
const width = '100%';

// Custom checkbox look for the modern (Dimensions-table-only) filter popup —
// plain sx overrides so it doesn't depend on the shared 'filters'/'delete'
// checkbox theme variants used elsewhere in the app.
//
// `#D9D9D9` (borders below) and `#708090` (placeholder) have no exact match
// in `styles/revampTokens.ts` and are kept as literals rather than snapped to
// the nearest token, which would silently shift the rendered color.
const modernCheckboxSx = {
  '.chakra-checkbox__control': {
    borderColor: accent.border,
    borderWidth: '1.2px',
    borderRadius: '4px',
    width: '14px',
    height: '14px',
  },
  '.chakra-checkbox__control[data-checked], .chakra-checkbox__control[data-indeterminate]': {
    background: accent.solid,
    borderColor: accent.solid,
    color: 'white',
  },
};

interface FilterItem {
  name: string;
  status: boolean;
  search: boolean;
}

interface ModernFiltersPopupProperties {
  checkboxData: any;
  searchValue: string | undefined;
  onSearchChange: (value: string) => void;
  visibleItems: FilterItem[];
  allChecked: boolean;
  isIndeterminate: boolean;
  onAllCheckBoxChange: (event: any) => void;
  onCheckBoxChange: (user: string, event: any) => void;
  onCancel: () => void;
  onApply: () => void;
}

const ModernFiltersPopup = ({
  checkboxData,
  searchValue,
  onSearchChange,
  visibleItems,
  allChecked,
  isIndeterminate,
  onAllCheckBoxChange,
  onCheckBoxChange,
  onCancel,
  onApply,
}: ModernFiltersPopupProperties) => {
  const ModernRow = useCallback(
    (properties: any) => {
      const { index, style, data } = properties;

      return (
        <Box
          style={style}
          key={index}
          onMouseMove={(event: any) => event.stopPropagation()}
          onPointerDown={(event: any) => event.stopPropagation()}
          px='14px'
          display='flex'
          alignItems='center'
        >
          <Checkbox
            isChecked={Boolean(data[index]?.status)}
            onChange={(event) => onCheckBoxChange(data[index]?.name, event)}
            sx={modernCheckboxSx}
          >
            <Text
              maxW='150px'
              className='text-overflow'
              fontSize='12px'
              fontFamily='Roboto Regular'
              color={primaryAction.bg}
            >
              {data[index]?.name}
            </Text>
          </Checkbox>
        </Box>
      );
    },
    [onCheckBoxChange],
  );

  return (
    <Stack
      onMouseMove={(event: any) => event.stopPropagation()}
      minW='252px'
      maxW='252px'
      position='relative'
      spacing={0}
    >
      <Box
        position='sticky'
        top='0'
        backgroundColor='white'
        zIndex={1}
        display='flex'
        flexDirection='column'
        px='14px'
        pt='14px'
      >
        <Flex alignItems='center' gap='9px' mb='13px'>
          <FigmaFilterIcon width={14} height={14} color={accent.solid} />
          <Text
            fontSize='13px'
            fontWeight='500'
            fontFamily='Roboto Medium, Roboto Regular'
            color={primaryAction.bg}
          >
            {checkboxData?.text}
          </Text>
        </Flex>
        <Flex
          alignItems='center'
          gap='6px'
          bg='white'
          border='1px solid #D9D9D9'
          borderRadius='8px'
          px='10px'
          py='7px'
          mb='11px'
        >
          <FigmaSearchIcon width={12} height={12} color={accent.solid} />
          <Input
            variant='unstyled'
            placeholder='Search'
            fontSize='12px'
            fontFamily='Roboto Regular'
            color={primaryAction.bg}
            _placeholder={{ color: '#708090' }}
            onChange={(event: any) => onSearchChange(event.target.value)}
          />
        </Flex>
        {!searchValue && (
          <Checkbox
            isChecked={allChecked}
            isIndeterminate={isIndeterminate}
            onChange={onAllCheckBoxChange}
            sx={modernCheckboxSx}
            mb='9px'
          >
            <Text
              fontSize='12px'
              fontWeight='500'
              fontFamily='Roboto Medium, Roboto Regular'
              color={primaryAction.bg}
            >
              Select all
            </Text>
          </Checkbox>
        )}
      </Box>
      <Stack maxH='162px' overflowX='auto' spacing={0}>
        <List
          height={height}
          itemData={visibleItems}
          itemCount={visibleItems?.length}
          itemSize={itemHeight}
          width={width}
          className='filters_scroll'
        >
          {ModernRow}
        </List>
      </Stack>
      <Flex gap='9px' px='14px' pb='14px' pt='11px' mt='9px' borderTop='1px solid #E8E9EB'>
        <Button
          onClick={onCancel}
          variant='unstyled'
          flex='1'
          bg='white'
          border='1px solid #D9D9D9'
          borderRadius='8px'
          color={primaryAction.bg}
          fontSize='12px'
          fontWeight='500'
          h='32px'
          _hover={{ bg: accent.subtle }}
        >
          Cancel
        </Button>
        <Button
          onClick={onApply}
          variant='unstyled'
          flex='1'
          bg={accent.solid}
          borderRadius='8px'
          color='white'
          fontSize='12px'
          fontWeight='500'
          h='32px'
          _hover={{ bg: accent.hover }}
        >
          Apply
        </Button>
      </Flex>
    </Stack>
  );
};

export default ModernFiltersPopup;
